import { showAlert, userConfirmation } from 'BMapsSrc/utils';
import { EventBroker } from 'BMapsSrc/eventbroker';
import { isWaterAtomDrawable } from 'BMapsSrc/visualizations';
import { App } from '../BMapsApp';
import { ServerCmds } from '../server_cmds';
import { WaterStates } from '../model/DisplayState';
import { UserActions } from './UserCmd';

/**
 * @fileoverview Offering to derive a case's water map, and showing the result.
 *
 * The server no longer derives a map while loading a case -- that took minutes with nothing
 * on screen -- so the client asks for one, once the user has agreed to the wait. Two callers:
 * the right control panel's water buttons, which act on every displayed protein, and a case's
 * Water row in the Fragments tree, which acts on that case alone. Both pass the cases in.
 */

/**
 * @description Is a water map already being derived for this case? Asked of the connection's
 * own command queue rather than tracked here: the queue records every command from the moment
 * it is sent until its Completed arrives, keyed by case through the request arguments, and it
 * clears itself when the connection resets (ServerCmdProcessingQueue.rejectAll, on a socket
 * drop or a zapAll). A module-level flag could do none of those -- it could not name the case,
 * which the per-case tree row needs, and it depended on its own finally running to clear.
 *
 * The window this does NOT cover is between the user accepting the offer and the command
 * reaching the queue (doWithActiveSolute may send a set-active-solute first). A second click
 * inside that window queues a second derive-watermap; the server answers the second from the
 * map the first wrote, so the cost is one wasted round trip.
 *
 * It also assumes derive-watermap stays SYNCHRONOUS: an entry leaves the queue when its
 * Completed arrives, which today means the map has been written. If the command ever moves to
 * the async-response-info pattern, Completed will arrive up front and this will answer "not in
 * flight" while the server is still deriving.
 * @param {import('BMapsModel').CaseData} caseData
 * @returns {boolean}
 */
export function watermapDerivationInFlight(caseData) {
    const { connector, mapCase } = App.getDataParents(caseData);
    if (!connector || !mapCase) return false;
    return connector.commandQueue.some(({ name, args }) => (
        name === ServerCmds.DeriveWatermap.cmdName && args?.[0]?.spec === mapCase.projectCase
    ));
}

/**
 * @description Is this case still in the workspace? A derivation runs for minutes, and a zap
 * or a structure removal during it leaves the offer holding cases that no longer exist.
 * DataConnection.zap empties the case collections as soon as the zap is asked for; a removal
 * takes its case out once the server confirms.
 * @param {import('BMapsModel').CaseData} caseData
 * @returns {boolean}
 */
function isStillLoaded(caseData) {
    return App.Workspace.allCaseData().includes(caseData);
}

/**
 * @description How many computed waters these cases hold, and how many of them the display
 * would actually draw. Those are different numbers: a whole map can sit above the cutoff
 * isWaterAtomDrawable applies, so a derivation can succeed and show nothing.
 * @param {import('BMapsModel').CaseData[]} caseDataList
 * @returns {{waters: number, drawable: number}} counted per water molecule
 */
export function computedWaterCounts(caseDataList) {
    let waters = 0;
    let drawable = 0;
    for (const caseData of caseDataList) {
        for (const water of caseData.getComputedWaters()) {
            waters += 1;
            if (water.getAtoms().some(isWaterAtomDrawable)) drawable += 1;
        }
    }
    return { waters, drawable };
}

/**
 * @description Offer to derive a water map for the given cases, and show the waters once one
 * arrives. Deriving is minutes of server work that blocks other commands, so it never starts
 * without the user saying yes.
 * @param {import('BMapsModel').CaseData[]} caseDataList cases to offer for; the caller decides
 *      which are derivable (CaseData.canDeriveWatermap)
 * @param {{
 *      targetState?: string, perCase?: boolean, noneMessage?: string,
 *      confirm?: Function, alert?: Function
 * }} options targetState is the water view to switch to once a map arrives; perCase shows
 *      each derived case's own Water row instead, leaving the view alone, which is what a
 *      per-protein caller wants; noneMessage is the alert for an empty list; confirm and alert
 *      are the dialog seam the tests drive.
 */
export async function offerWatermapDerivation(caseDataList, {
    targetState=WaterStates.computed,
    perCase=false,
    noneMessage='No computed BMaps waters available.',
    confirm=userConfirmation,
    alert=showAlert,
}={}) {
    try {
        if (caseDataList.length === 0) {
            alert('Water Display', noneMessage);
            return;
        }

        // Anything already being derived is left alone rather than asked for again. Per case,
        // so a second protein can still be offered while the first is running -- and so the
        // user is told which, instead of the click being silently dropped.
        const busy = caseDataList.filter(watermapDerivationInFlight);
        const toDerive = caseDataList.filter((cd) => !busy.includes(cd));
        if (toDerive.length === 0) {
            const names = busy.map((cd) => cd.getName()).join(', ');
            alert('Water Display', `A water map is already being derived for ${names}.`);
            return;
        }

        const several = toDerive.length > 1;
        const names = toDerive.map((cd) => cd.getName()).join(', ');
        const ok = await confirm('Derive water map?', [
            `Water simulation data are available for ${names}, but no water map has been derived yet.`,
            several
                ? `Deriving ${toDerive.length} water maps takes several minutes each, during which other requests wait.`
                : 'Deriving a water map takes several minutes, during which other requests wait.',
            several ? 'Derive them now?' : 'Derive it now?',
        ]);
        if (!ok) return;

        // Ask again, now the dialog has closed. The test above ran BEFORE it opened, and two
        // offers can sit in their dialog phase at once -- a second click landing before the
        // backdrop renders, or the water buttons and a tree row together. Without this the
        // later offer sends a derivation the earlier one already has in flight; the server
        // answers it from the map the first wrote, so the cost is a wasted round trip.
        const toSend = toDerive.filter((cd) => !watermapDerivationInFlight(cd));
        if (toSend.length === 0) return;

        const { errors, derived } = await UserActions.DeriveWatermaps(toSend);

        // Nothing was sent at all, so there is no failure to report. Narrow -- the
        // confirmation is modal, so it takes a removal asked for between the click and the
        // first send -- but "No water map could be derived" would be a lie.
        if (derived.length === 0 && errors.length === 0) return;

        // A zap or a removal during the derivation takes the cases out of the workspace.
        // Then there is nothing to show and nobody it would make sense to tell: no alert, no
        // view change, no repaint. A rejection with the workspace intact -- a dropped socket --
        // still reports, through the errors below.
        if (!toSend.some(isStillLoaded)) return;

        // Ask the cases whether waters arrived. The command cannot answer that:
        // integrateFragments deliberately returns no atom groups for water maps, so counting
        // what it returned reported every successful derivation as a failure.
        const { waters, drawable } = computedWaterCounts(derived.filter(isStillLoaded));
        if (waters === 0) {
            alert('Water Display', errors.length > 0
                ? `No water map could be derived: ${errors.join('; ')}`
                : 'No water map could be derived for this structure.');
            return;
        }

        if (perCase) {
            // The Selector draws a computed water when the view shows all of them OR its case's
            // Water row is visible. Turning on the row shows this case alone and lights its eye.
            for (const caseData of derived.filter(isStillLoaded)) {
                UserActions.ToggleVisibility(caseData.getComputedWaterCollection(), true);
            }
        } else {
            UserActions.SetWaterView(targetState);
        }
        // SetWaterView is not enough on its own. updateView runs the water display pass only
        // when the value CHANGED, and the caller may already have set it to the target before
        // the derivation started -- so the waters would sit integrated and out of the scene.
        // This is the internal repaint route; it is not a user action.
        EventBroker.publish('redrawSceneRequest');

        const notes = [];
        if (errors.length > 0) notes.push(`Some water maps could not be derived: ${errors.join('; ')}`);
        if (drawable === 0) {
            // Silence here is the worst outcome of the lot: the user agreed to wait minutes
            // and gets an empty canvas with the view switched on, looking like a broken map.
            notes.push(`The derived water map has ${waters} water${waters === 1 ? '' : 's'},`
                + ' but none of them is below the display threshold, so nothing is drawn.');
            notes.push('Waters at or above the excess chemical potential of bulk water are hidden.');
        }
        if (notes.length > 0) alert('Water Display', notes);
    } catch (err) {
        // Fired without a handler by both callers, so anything thrown here -- by a dialog, or
        // by the counting -- would otherwise be an unhandled rejection.
        alert('Water Display', `Water map derivation failed: ${err?.message || err}`);
    }
}
